import { peSponsorLogos, teamLogoMarquee } from "./logoSets";

/**
 * Homepage content extracted from the Automad theme.
 * Translation: Homepage content extracted from the Automad theme.
 */
export const siteSections = {
  availabilityBadge: "Available for Q3 2026",
  hero: {
    titleAccent: "Forward-Deployed ",
    titleMain: "Engineering.",
    description:
      "We embed elite data & AI teams directly into your organization. " +
      "We don't deliver slide decks-we ship production code that compounds value.",
    primaryCta: {
      label: "START ENGAGEMENT",
      href: "#contact",
    },
    secondaryCta: {
      label: "SEE OUR WORK",
      href: "#work",
    },
  },
  manifesto: {
    eyebrow: "Data Chimp Labs",
    lines: [
      "Healthcare data innovation",
      "through deep engineering",
      "rethinking every layer of",
      "intelligence.",
    ],
    accentWords: ["deep", "engineering"],
    cta: {
      label: "Discover Our Philosophy",
      href: "#about",
    },
  },
  problems: {
    eyebrow: "The Bottlenecks",
    title: "Why Healthcare Innovation Stalls",
    cards: [
      {
        title: "Manual Operations",
        icon: "FileSpreadsheet",
        description:
          "Spreadsheets running critical workflows. Billing leakage through " +
          "cracks in manual processes. Zero visibility into real-time unit economics.",
      },
      {
        title: "Talent Gap",
        icon: "Users",
        description:
          "Can't hire top-tier data engineers in healthcare. Contractors deliver " +
          "prototypes, not production. Internal teams lack AI specialization.",
      },
      {
        title: "AI Out of Reach",
        icon: "Zap",
        description:
          "Hype without execution. Concerns about HIPAA compliance paralyze " +
          "progress. Inability to operationalize LLMs beyond chat interfaces.",
      },
    ],
  },
  privateEquity: {
    id: "pe",
    eyebrow: "For Private Equity",
    title: "We speak the language of Value Creation.",
    titleAccent: "Value Creation",
    description:
      "Portfolio companies need to move fast. You need to demonstrate operational " +
      "improvement and build reporting for sponsors-often without internal data talent.",
    sponsorsLabel: "Portfolio Sponsors",
    sponsorLogos: peSponsorLogos,
    headlineMetric: {
      value: 500,
      prefix: "$",
      suffix: "M+",
      label: "Enterprise Value Added",
      description:
        "Measuring the direct impact of data infrastructure on portfolio " +
        "valuation. Our platform quantifies operational improvements and efficiency " +
        "gains through systematic data orchestration.",
    },
    speedToValue: {
      label: "Speed to Value",
      badge: "Day One Ready",
      headline:
        "Every month spent ramping a team is a month your value creation plan sits idle.",
      description:
        "We embed and execute immediately - no recruiting, no onboarding, no " +
        "12-month ramp to first results.",
      bars: [
        { label: "Data Chimp Labs", suffix: "Weeks", base: 10, target: 15, accent: true },
        {
          label: "Traditional Consultancy",
          suffix: "Months",
          base: 40,
          target: 60,
        },
        { label: "Internal Hire", suffix: "12-18 Months", base: 60, target: 90 },
      ],
    },
    cards: [
      {
        type: "donut",
        eyebrow: "EBITDA Margin",
        title: "Precision Analysis & Leakage Identification",
        target: 24.5,
        suffix: "%",
        decimals: 1,
        description:
          "Identifying hidden leakage in supply chains, automating manual billing, " +
          "and providing the granular visibility required to expand margins by " +
          "200-400bps through data-driven operational decisions.",
        footer: "Active Optimization",
      },
      {
        type: "counter",
        eyebrow: "Data Governance",
        displayTarget: 100,
        suffix: "%",
        description:
          "Standardizing metadata architecture and data lineage across disparate " +
          "portfolio systems. We ensure total regulatory compliance, immutable data " +
          "integrity, and a single source of truth that simplifies due diligence and " +
          "prepares assets for premium exit valuations.",
      },
      {
        type: "text",
        eyebrow: "P&L Visibility",
        headline: "Real-time",
        description:
          "Eliminating the friction of monthly close cycles. Our real-time " +
          "infrastructure provides instant visibility into cash flow, revenue " +
          "pacing, and expense management across all business units, enabling " +
          "proactive rather than reactive capital allocation.",
        footer: "Live Feed Active",
      },
    ],
    boardReadiness: {
      eyebrow: "Board Readiness",
      title: "Consolidated Sponsor Feeds",
      description:
        "Automating the heavy lifting of portfolio reporting. We build " +
        "high-fidelity data feeds that deliver real-time metrics directly to " +
        "sponsors, eliminating manual deck preparation and ensuring board members " +
        "always have the truth at their fingertips.",
      footer: "Signal Status: Broadcasting",
    },
    exitReadiness: {
      eyebrow: "Exit Readiness",
      title: "Knowledge Transfer",
      description:
        "Building for the next owner, not just for today. We provide full " +
        "codebase documentation, technical handovers, and talent training to " +
        "ensure internal ownership and total technical independence upon exit - " +
        "maximizing valuation through IP maturity.",
      status: "Migration: Active",
      labels: ["Labs Core", "Internal Core"],
      footnotes: ["Documentation", "Talent Training", "IP Handover"],
    },
  },
  impact: {
    eyebrow: "Systematic Performance",
    title: "Engineering Excellence at Scale",
    description:
      "Quantitative precision across the entire healthcare data lifecycle. From " +
      "sub-second real-time processing to enterprise-scale data governance, our " +
      "infrastructure is engineered for absolute consistency.",
    cards: [
      {
        title: "Revenue Growth",
        value: "1,300%",
        description:
          "Systematic increase in client revenue through optimized data pipelines and billing automation.",
      },
      {
        title: "Patient Engagement",
        value: "34%",
        description:
          "Measurable boost in patient retention and adherence through intelligent engagement frameworks.",
      },
      {
        title: "Valuation Multiplier",
        value: "+10X",
        description:
          "Enterprise value added through robust, scalable data asset creation and IP generation.",
      },
      {
        title: "Clinic Throughput",
        value: "25%",
        description:
          "Operational efficiency gains through automated scheduling and clinical workflow optimization.",
      },
      {
        title: "Market Reach",
        value: "900%",
        description:
          "Expansion in addressable market via scalable multi-tenant architecture.",
      },
      {
        title: "OpEx Reduction",
        value: "-15%",
        description:
          "Decrease in overhead through elimination of manual data entry and reconciliation.",
      },
      {
        title: "Data Governance",
        value: "100%",
        description:
          "End-to-end data lineage and HIPAA-compliant architecture for institutional confidence.",
      },
    ],
  },
  services: {
    eyebrow: "Our Expertise",
    title: "Engineering Capabilities",
    cards: [
      {
        title: "Fractional CDO",
        description:
          "Strategic data leadership without the full-time hire. Enterprise strategy, vendor management, and board reporting.",
        bullets: [
          "Enterprise Roadmap",
          "Team Hiring & Mgmt",
          "Board Reporting",
        ],
      },
      {
        title: "AI Product Development",
        description:
          "Custom LLM applications with HIPAA compliance baked in. Automated financial reconciliation, intelligent scheduling, and full-stack delivery.",
        bullets: [
          "Custom LLM Apps",
          "Financial Reconciliation",
          "HIPAA Compliance",
        ],
      },
      {
        title: "Data Platforms",
        description:
          "Modern cloud architecture that scales. AWS/Azure native, EHR integrations, real-time CDC, and self-service BI.",
        bullets: ["AWS/Azure Native", "EHR Integration", "Real-time CDC"],
      },
    ],
  },
  work: {
    eyebrow: "Selected Engagements",
    title: "Proven Engineering in the Field",
    description:
      "From early-stage clinical builds to rapid turnaround for private equity sponsors, we deliver production-grade data systems that simplify complexity and compound value.",
    cases: [
      {
        id: "case1",
        number: "01",
        category: "Full-Stack Build · Personal Injury & Workers' Comp",
        title: "Custom Practice Management Suite",
        challenge:
          "Multi-specialty medical practice managing high-volume attorney and " +
          "provider referrals through disparate spreadsheets and manual processes - " +
          "leading to dropped cases, delayed treatments, and billing leakage.",
        solution:
          "Custom Practice Management Suite replacing fragmented workflows. " +
          "Unified referral tracking from intake to case closure. " +
          "EHR and scheduling system integration (CareCloud). " +
          "Automated communication workflows with referring partners. " +
          "Real-time case status visibility and reporting. " +
          "Private HIPAA-compliant AI engine that surfaces patient treatment plans, generates case timelines, reconciles transactions, and profiles payor risk - all natively within the platform.",
        outcome:
          "Single source of truth for all referral and case data. Staff can handle higher volumes with greater accuracy while management has actionable insights into the business.",
      },
      {
        id: "case2",
        number: "02",
        category: "AI Product Build",
        title: "DispoRx — ER Efficiency Platform",
        challenge:
          "Bottlenecks in emergency patient flow, communication breakdowns, and manual processes delaying care decisions.",
        solution:
          "Real-time patient flow management, AI-powered transcription, automated handoffs, and actionable dashboards for ER metrics.",
        outcome: "$250K annual savings. Reduced decision-making time.",
        cta: {
          label: "View Project ->",
          href: "https://www.disporx.com/",
        },
      },
      {
        id: "case3",
        number: "03",
        category: "Fractional CDO",
        title: "PE-Backed Physical Therapy Platform",
        challenge:
          "700K+ annual visits, fragmented data across EMR/billing/scheduling, no real-time visibility into unit economics.",
        solution:
          "AWS data platform, real-time CDC, unified warehouse, predictive models, and board-ready reporting.",
        outcome: "CFO has real-time visibility into clinic-level profitability.",
      },
      {
        id: "case4",
        number: "04",
        category: "AI Product Build",
        title: "AI Clinical Documentation System",
        challenge:
          "Psychiatrists spending hours on documentation instead of patient care.",
        solution:
          "HIPAA-compliant PWA with voice-to-note dictation, LLM-generated SOAP notes with embedded clinical guidance, prior authorization scoring, automated CPT justification, and AdvancedMD integration.",
        outcome: "MVP pilot live, architected for enterprise scale.",
      },
      {
        id: "case5",
        number: "05",
        category: "Data Platform",
        title: "Mental Health Practice Transformation",
        challenge:
          "Manual Excel tracking, no KPI visibility, scaling bottlenecks.",
        solution:
          "Automated AdvancedMD extraction, Azure Synapse OLAP model, real-time BI dashboards, data governance framework.",
        outcome:
          "Real-time provider productivity and patient outcome tracking.",
      },
    ],
  },
  textHighlights: [
    "Manual Operations",
    "Talent Gap",
    "AI Out of Reach",
    "Every month spent ramping a team is a month your value creation plan sits idle.",
    "Precision Analysis & Leakage Identification",
    "Real-time",
    "Data Governance",
    "Consolidated Sponsor Feeds",
    "Knowledge Transfer",
    "Valuation Multiplier",
    "Fractional CDO",
    "Custom Practice Management Suite",
    "DispoRx — ER Efficiency Platform",
    "PE-Backed Physical Therapy Platform",
    "AI Clinical Documentation System",
    "Mental Health Practice Transformation",
    "Data-Driven Value Creation in Private Equity: Unlocking EBITDA Velocity in 2026",
    "Ethical AI Product Development: Building Trustworthy Clinical Assistants",
    "Modernizing Healthcare Data Pipelines",
    "contact@datachimplabs.com",
  ],
  about: {
    eyebrow: "The Team",
    title: "We ship production code. \n Not prototypes.",
    description:
      "Our team backgrounds include NASA, General Dynamics, DOD, Visa, PayPal, eBay, and BlackRock. \n We bring engineering discipline to healthcare.",
    logos: teamLogoMarquee,
  },
  blog: {
    eyebrow: "Insights",
    title: "Latest from Data Chimp Labs",
    ctaLabel: "View All Posts",
  },
  footer: {
    title: "Ready to execute?",
    description:
      "We are currently accepting waitlist engagements for Q3 2026. \n Reach out to discuss your infrastructure needs.",
    contactEmail: "contact@datachimplabs.com",
  },
  codeSimulation: [
    { text: "// Real-time Health Index (HRI) Core Integration\n", token: "comment" },
    { text: "async function ", token: "keyword" },
    { text: "analyzePatientTelemetry", token: "function" },
    { text: "(vitals, history) {\n  ", token: "plain" },
    { text: "const", token: "keyword" },
    { text: " engine = await DataChimp.", token: "plain" },
    { text: "ML.loadEngine", token: "function" },
    { text: '("clinical-v4");\n\n  ', token: "plain" },
    { text: "const", token: "keyword" },
    { text: " telemetry = {\n    heartRate: vitals.bpm / ", token: "plain" },
    { text: "200", token: "number" },
    { text: ",\n    oxygen: vitals.o2 / ", token: "plain" },
    { text: "100", token: "number" },
    { text: ",\n    bloodPressure: vitals.bp.sys / ", token: "plain" },
    { text: "180", token: "number" },
    { text: "\n  };\n\n  // Execute pattern recognition\n  ", token: "plain" },
    { text: "const", token: "keyword" },
    { text: " result = await engine.", token: "plain" },
    { text: "predict", token: "function" },
    { text: "({\n    input: telemetry,\n    temporal: history.", token: "plain" },
    { text: "slice", token: "function" },
    { text: "(-24),\n    threshold: ", token: "plain" },
    { text: "0.85", token: "number" },
    { text: "\n  });\n\n  if (result.isHazardous) {\n    ", token: "plain" },
    { text: "await", token: "keyword" },
    { text: " AlertSystem.", token: "plain" },
    { text: "dispatch", token: "function" },
    { text: '({\n      priority: "CRITICAL",\n      payload: result.metadata\n    });\n  }\n\n  ', token: "plain" },
    { text: "return", token: "keyword" },
    { text: " DataChimp.Utils.normalize({ raw: result.healthIndexScore, adaptive: true });\n}", token: "plain" },
  ],
};
